import { useState } from 'react';
import automobyApi from '../Api';
import { useUpdateEffect } from './useUpdateEffect';
import { useUpdateQueryParams, useReadQueryParams } from './useQueryParams';

export const useBookingDetails = (defaultPageSize) => {
  const updateQueryParams = useUpdateQueryParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({
    all: true,
    limit: defaultPageSize,
    order_by: "id",
    page: 1,
    booking_id: 0,
    is_active: true,
    search: "",
  });

  useUpdateEffect(() => {
    fetchList();
  }, [filter]);

  const fetchList = async () => {
    try {
      updateQueryParams(filter);
      const response = await automobyApi.post("/booking-details/list", filter);
      setItems(response.data.bookingDetails);
      setTotal(response.data.total);
      return response.data.bookingDetails;
    } catch (error) {
      console.error("Error fetching booking details list:", error);
    }
  };

  const fetchAll = async () => {
    try {
      const requestData = {
        all: true,
        limit: 100,
        order_by: "id",
        offset: 1
      };
      const response = await automobyApi.post("/booking-details/all", requestData);
      setItems(response.data.bookingDetails);
      setTotal(response.data.total);
      return response.data.bookingDetails;
    } catch (error) {
      console.error("Error fetching all booking details:", error);
    }
  };

  const add = async (newData) => {
    try {
      const response = await automobyApi.post("/booking-details/add", newData);
      fetchList();
      return response.data;
    } catch (error) {
      console.error("Error adding booking details:", error);
      throw error;
    }
  };

  const update = async (updatedData) => {
    try {
      const response = await automobyApi.post("/booking-details/update", updatedData);
      fetchList();
      return response.data;
    } catch (error) {
      console.error("Error updating booking details:", error);
      throw error;
    }
  };

  const remove = (id) => {
    return new Promise(async (resolve, reject) => {
      try {
        const requestData = { id };
        const response = await automobyApi.post("/booking-details/delete", requestData);
        fetchList();
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getById = (id) => {
    return new Promise(async (resolve, reject) => {
      try {
        const requestData = { id };
        const response = await automobyApi.post("/booking-details/get-by-id", requestData);
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  };

  const search = async (searchTerm, count) => {
    try {
      const requestData = {
        search: searchTerm,
        count: count,
      };
      const response = await automobyApi.post("/booking-details/search", requestData);
      return response.data;
    } catch (error) {
      console.error("Error searching booking details:", error);
      throw error;
    }
  };

  const uploadBillOfLanding = async (fileData) => {
    try {
      const response = await automobyApi.post("/booking-details/bill-of-landing/upload", fileData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading bill of landing:", error);
      throw error;
    }
  };

  const uploadAgentInvoice = async (fileData) => {
    try {
      const response = await automobyApi.post("/booking-details/agent-invoice/upload", fileData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading agent invoice:", error);
      throw error;
    }
  };

  const getByBookingId = (bookingId) => {
    return new Promise(async (resolve, reject) => {
      try {
        const requestData = { booking_id: bookingId };
        const response = await automobyApi.post("/booking-details/by-booking", requestData);
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  };

  const BookingDetailsService = {
    items,
    total,
    filter,
    setFilter,
    fetchList,
    fetchAll,
    add,
    update,
    remove,
    getById,
    search,
    uploadBillOfLanding,
    uploadAgentInvoice,
    getByBookingId,
  };

  return { BookingDetailsService };
};